/**
 * "Open the chat for this thing" — the request channel a surface OUTSIDE the
 * chat's React tree uses to bring Prest0n up beside itself (owner directive
 * 2026-08-24, round 2: a file preview offers to open the conversation the
 * file came from, and the two sit side by side).
 *
 * Shaped deliberately like its sibling `prest0n:chat-new-thread-send`
 * (GlobalChatSystem.tsx): a CANCELABLE document event, where
 * `preventDefault()` is the "a visible chat took it" handshake — so a caller
 * on a route where the chat is mounted-but-hidden learns nothing happened
 * and can fall back, instead of silently doing nothing.
 *
 * Dock side: THERE IS NO DOCK SIDE (owner directive 2026-08-20;
 * CHAT_LAYOUT.DOCK_SIDE). `forceDocked` asks for the docked RIGHT rail so the
 * requesting surface keeps the rest of the screen — "beside" always means the
 * pane on the right, the requester to its left. No side field exists here, by
 * design.
 *
 * Mobile is handled by the reducer, not by callers: MOBILE NEVER DOCKS, and
 * opening on a phone forces the fullscreen takeover — so this request
 * degrades on its own to "open the chat on that conversation".
 */

export const CHAT_OPEN_THREAD_EVENT = 'prest0n:chat-open-thread';

export interface ChatOpenThreadDetail {
  /**
   * The conversation to activate. Omitted when the requester cannot know it
   * (a file opened from a bare link has no message behind it) — the chat then
   * opens on whatever thread is already active, which is still the requested
   * side-by-side view.
   */
  threadId?: string;
  /**
   * Label for `switchThread`'s minimal-record path, used only when the id has
   * no local record left (archived, or another device's thread).
   */
  fallbackTitle?: string;
  /**
   * A presentation, not the person's own gesture (chatModeMemory.ts; Miles, 2026-10-15): the open moves THIS
   * tab only and never their default (a thread's deep link at boot, the voice pulling up the thread it is
   * working in). Absent: a person's click asked for it, and it counts as their choice, as before.
   */
  presentation?: boolean;
  /** Ask for the docked right rail so the requester keeps the rest of the screen. */
  forceDocked?: boolean;
}

/**
 * Dispatch the request. Returns true when a visible chat claimed it.
 * SSR/jsdom-safe: no document → nothing to ask, returns false.
 */
export function requestChatOpenForThread(detail: ChatOpenThreadDetail = {}): boolean {
  if (typeof document === 'undefined') return false;
  const event = new CustomEvent<ChatOpenThreadDetail>(CHAT_OPEN_THREAD_EVENT, {
    detail,
    cancelable: true,
  });
  document.dispatchEvent(event);
  return event.defaultPrevented;
}
